import React from "react";
import { Paper, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import RadialBarChart from "../../../Charts/RadialBarChart";
import StackedBarChartWithDifference from "../../../MixedCharts/StackedBarChartWithDifference";

const useStyles = makeStyles(() => ({
    loyaltyScoreText: {
        background: "white",
        color: "grey",
        fontSize: 10,
        width: "90%",
    },
    subHeader: {
        fontWeight: 400,
    },
}));

const tooltips = [
    "Average work experience of current employees in the company affects the overall Loyalty Score the most. It is an indication of how long employees are currently working in the company.",
    "Ratio between boomerang employees and the total number of employees. Having boomerang employees is an indication of a good company culture to which former employees wish to return to.",
    "Ratio between current employees in the company and the former employees within the same company. It shows how the average work experience changes between the two sets of employees.",
    "Ratio between the average work experience of the employees in the current company and the average work experience of these employees in previous companies.",
    "Ratio between current and former employees. Lower ratio signals that the number of current employees is significantly lower than the number of former employees, indicating that the company has a high turnover.",
    "Ratio between full-time employees and associate employees who are hired as consultants. Full-time employees are considered more loyal because they stick to one company.",
];

export function Section1({ stacks, data }) {
    const classes = useStyles();

    return (
        <Paper>
            <Typography variant="h5" color="textSecondary" pl={1.5} pt={1.25}>
                Loyalty Score
            </Typography>
            <Stack>
                <Stack width="100%" direction="row" mb={1} mt={3}>
                    <Stack flex={1} alignItems="center">
                        <Typography variant="h6" color="textSecondary" ml={5}>
                            {stacks[0].stack.name}
                        </Typography>
                    </Stack>

                    <Stack flex={1} alignItems="center">
                        <Typography variant="h6" color="textSecondary" mr={7}>
                            {stacks[1].stack.name}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" justifyContent="center">
                    <RadialBarChart value={data.averageScore.stack1.series.value} />
                    <RadialBarChart value={data.averageScore.stack2.series.value} />
                </Stack>
            </Stack>
            <Stack>
                <Stack height={255}>
                    <Typography variant="h5" color="textSecondary" pl={1.5} pt={1.25} className={classes.subHeader}>
                        Score Metrics
                    </Typography>
                    <StackedBarChartWithDifference
                        chartInfo={{ title: "", shortTitle: "" }}
                        stacks={stacks}
                        firstStackSeries={[data.averageSubScores.comparisonChart.stack1.series]}
                        secondStackSeries={[data.averageSubScores.comparisonChart.stack2.series]}
                        categories={data.averageSubScores.comparisonChart.categories}
                        differenceSeries={[{ data: data.averageSubScores.diffChart.series.data }]}
                        stackedBarColors={[
                            ["#4973EB", "#7095FF", "#92AEFF", "#ADC2FF", "#BCCDFF", "#D1DDFF"],
                            ["#4BC186", "#62DA9E", "#84ECB8", "#A3F4CC", "#A9F8D0", "#D4FFEA"],
                        ]}
                        diffBarColors={["#7645c3", "#8f5edd", "#9366d9", "#986cdc", "#a782e1", "#b692ef"]}
                        itemsPerPage={6}
                        distributed
                        inPercentage={false}
                        showLegend={false}
                        elevation={0}
                        tooltips={data.averageSubScores.comparisonChart.categories.map((category, index) => ({
                            title: category,
                            message: tooltips[index],
                        }))}
                        enableCustomTooltip
                        containerStyle={{ pt: 4.6, width: 166, mr: -0.7, px: 1 }}
                        padding={[null, null, { bottom: 5, top: -27 }]}
                        diffHeight="90%"
                        chartStyles={{ mt: 0, ml: 16 }}
                        flexRatios={[7, 2]}
                        max={1}
                        tickAmount={4}
                        noFormats
                        isDistributed
                        showActions={false}
                        v2
                    />
                </Stack>
                <Stack margin="auto" width="100%" backgroundColor="white" alignItems="center" mt={8} pb={4}>
                    <Typography variant="body3" align="center" className={classes.loyaltyScoreText} display="inline">
                        *Loyalty Scores are calculated based on the Score Metrics, where each Score Metric has its own
                        contribution to the overall Loyalty Score.
                    </Typography>
                    <Typography variant="body3" align="center" className={classes.loyaltyScoreText} display="inline">
                        Loyalty Score Metrics in the chart are weighted and listed with the top metric having the
                        highest contribution and the bottom one having the lowest.
                    </Typography>
                </Stack>
            </Stack>
        </Paper>
    );
}
